import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import OfferContractTypesModal from "features/settings/general/OfferContractTypesComponent";
import { OfferTypeEnum } from "domain/offerType";
import { AllContractTypes, ContractType } from "domain/contract";
import CommoditiesModal from "features/settings/general/CommoditiesComponent";
import Commodity from "domain/commodity";
import ConfirmationModal, { useConfirmationModal } from "common/ui/ConfirmationModal";
import { AgGridReact } from "@ag-grid-community/react";
import defaultGridOptions from "common/agGrid/defaultGridOptions";
import columnBuilder from "common/agGrid/columnBuilder";
import RenderParameters from "common/agGrid/renderParameters";
import TypedSelect from "common/ui/TypedSelect";
import { CustomField, CustomFieldGroup, CustomFieldOption, CustomFieldType } from "domain/customField";
import InfoIcon from "common/ui/InfoIcon";
import CustomFieldOptionsModal from "./CustomFieldOptionsModal";
import NumberEditor from "common/ui/NumberEditor";
import { formatDate } from "common/formatters";

export interface ManageCustomFieldsProps {
    commodities: Commodity[];
    customFields: CustomField[];
    customFieldGroups: CustomFieldGroup[];

    onAdd(): void;
    onUpdate(customField: CustomField): Promise<any>;
    onRemove(customField: CustomField): Promise<any>;
}

const ManageCustomFields: React.FC<ManageCustomFieldsProps> = props => {
    const [showOffersContractsModal, setShowOffersContractsModal] = useState<boolean>(false);
    const [showCommoditiesModal, setShowCommoditiesModal] = useState<boolean>(false);
    const [showCustomFieldOptionsModal, setShowCustomFieldOptionsModal] = useState<boolean>(false);
    const confirmModal = useConfirmationModal();

    const [selectedCustomField, setSelectedCustomField] = useState<CustomField>(null);

    const OfferAndPricingTypes: OfferTypeEnum[] = [
        OfferTypeEnum.Cash,
        OfferTypeEnum.CashMinPrice,
        OfferTypeEnum.Hta,
        OfferTypeEnum.HtaMinPrice,
        OfferTypeEnum.BasisContract,
        OfferTypeEnum.FixBasisOnHta,
        OfferTypeEnum.FixFuturesOnBasis,
        OfferTypeEnum.DelayedPriceContract,
    ];

    const SellValue = "Sell";
    const BuyValue = "Buy";
    const BuySellValue = "Buy/Sell";
    const BuySellOptions: string[] = [
        BuySellValue,
        SellValue,
        BuyValue,
    ];

    // TODO: Add Support in DB for future
    const DateFormat = "yyyy-MM-DD";

    const CustomFieldTypeOptions: CustomFieldType[] = [
        CustomFieldType.String,
        // CustomFieldType.Int,
        CustomFieldType.Decimal,
        CustomFieldType.Date,
        // CustomFieldType.DateTime,
        // CustomFieldType.DateRange,
        CustomFieldType.SingleSelect,
        CustomFieldType.MultiSelect,
    ];

    function getColumns() {
        return [
            columnBuilder.build("#", null,
                {
                    valueGetter: (params: any) => `Custom Field #${params.data.sortOrder}`,
                    minWidth: 110,
                    maxWidth: 110,
                }),
            columnBuilder.build("Group", "group",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, CustomFieldGroup>) {
                        return <TypedSelect
                            className="w-100"
                            options={props.customFieldGroups?.filter(cfg => cfg.id !== 0)}
                            showNullRow
                            value={params.node.data.group}
                            valueGetter={cfg => cfg?.id}
                            textGetter={cfg => cfg?.name}
                            onValueSelected={value => onUpdate({ ...params.node.data, group: value })} />;
                    },
                    minWidth: 220,
                }),
            columnBuilder.build("ERP Id", "erpId",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.erpId}
                            placeholder={"ERP Id"}
                            required
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, erpId: ev.target.value })} />;
                    },
                    minWidth: 150,
                }),
            columnBuilder.build("Name", "name",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.name}
                            placeholder={"Name"}
                            required
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, name: ev.target.value })} />;
                    },
                    minWidth: 200,
                }),
            columnBuilder.build("Type", "type",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, CustomFieldType>) {
                        return <TypedSelect
                            className="w-100"
                            required
                            options={CustomFieldTypeOptions}
                            value={params.node.data.type}
                            valueGetter={cft => cft}
                            textGetter={cft => getTypeText(cft)}
                            onValueSelected={value => onUpdate({ ...params.node.data, type: value, minValue: null, maxValue: null, defaultValue: null })} />;
                    },
                    minWidth: 130,
                    maxWidth: 130,
                }),
            columnBuilder.build("Offers/Contract types", null,
                {
                    cellRenderer: function (params: RenderParameters<CustomField>) {
                        return <button className="link-button" onClick={() => openOfferContractsModal(params.node.data)} >
                            Offer/Contract types
                        </button>;
                    },
                    minWidth: 140,
                    maxWidth: 140,
                }),
            columnBuilder.build("Commodities", "commodities",
                {
                    cellRenderer: function (params: RenderParameters<CustomField>) {
                        return <button className="link-button" onClick={() => openCommoditiesModal(params.node.data)} >
                            Commodities
                        </button>;
                    },
                    minWidth: 100,
                    maxWidth: 100,
                }),
            columnBuilder.build("Allowed Options", "allowedOptions",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        if (!isAllowedOptionsFieldSupported(params.node.data))
                            return null;

                        return <button className="link-button" onClick={() => openAllowedOptionsModal(params.node.data)} >
                            Allowed Options
                        </button>;
                    },
                    minWidth: 120,
                    maxWidth: 120,
                }),
            columnBuilder.build("Buy/Sell", "buySell",
                {
                    cellRenderer: function (params: RenderParameters<CustomField>) {
                        return <TypedSelect
                            className="w-100"
                            options={BuySellOptions}
                            value={getBuySellValue(params.node.data)}
                            valueGetter={bs => bs}
                            textGetter={bs => bs}
                            onValueSelected={value => onBuySellValueSelected(params.node.data, value)} />;
                    },
                    minWidth: 90,
                    maxWidth: 90,
                }),
            columnBuilder.build("Min", "minValue",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        switch (params.node.data.type) {
                            case CustomFieldType.Date:
                                return <Form.Control
                                    type="date"
                                    value={formatDate(params.node.data.minValue, DateFormat)}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, minValue: ev.target.value })} />;

                            case CustomFieldType.Decimal:
                                return <NumberEditor
                                    type="number"
                                    ignoreInputGroupClassName={true}
                                    allowDecimals
                                    precision={4}
                                    placeholder={"0.0000"}
                                    value={params.node.data.minValue ? Number(params.node.data.minValue) : null}
                                    onValueChange={value => onUpdate({ ...params.node.data, minValue: value?.toString() })} />;

                            default:
                                return null;
                        }
                    },
                    minWidth: 200,
                    maxWidth: 200,
                }),
            columnBuilder.build("Max", "maxValue",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        switch (params.node.data.type) {
                            case CustomFieldType.Date:
                                return <Form.Control
                                    type="date"
                                    value={formatDate(params.node.data.maxValue, DateFormat)}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, maxValue: ev.target.value })} />;

                            case CustomFieldType.Decimal:
                                return <NumberEditor
                                    type="number"
                                    ignoreInputGroupClassName={true}
                                    allowDecimals
                                    precision={4}
                                    placeholder={"0.0000"}
                                    value={params.node.data.maxValue ? Number(params.node.data.maxValue) : null}
                                    onValueChange={value => onUpdate({ ...params.node.data, maxValue: value?.toString() })} />;

                            default:
                                return null;
                        }
                    },
                    minWidth: 200,
                    maxWidth: 200,
                }),
            columnBuilder.build("Default", "defaultValue",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        switch (params.node.data.type) {
                            case CustomFieldType.SingleSelect:
                                return <TypedSelect
                                    className="w-100"
                                    options={params.node.data.allowedOptions}
                                    showNullRow
                                    value={params.node.data.defaultValue}
                                    valueGetter={cfo => cfo.erpId ?? cfo}
                                    textGetter={cfo => cfo.description}
                                    onValueSelected={value => onUpdate({ ...params.node.data, defaultValue: value?.erpId })} />;

                            case CustomFieldType.Date:
                                return <Form.Control
                                    type="date"
                                    value={formatDate(params.node.data.defaultValue, DateFormat)}
                                    min={params.node.data.minValue ? formatDate(params.node.data.minValue, DateFormat) : null}
                                    max={params.node.data.maxValue ? formatDate(params.node.data.maxValue, DateFormat) : null}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, defaultValue: ev.target.value })} />;

                            case CustomFieldType.Decimal:
                                return <NumberEditor
                                    ignoreInputGroupClassName={true}
                                    allowDecimals
                                    precision={4}
                                    min={params.node.data.minValue ? Number(params.node.data.minValue) : null}
                                    max={params.node.data.maxValue ? Number(params.node.data.maxValue) : null}
                                    placeholder={"0.0000"}
                                    value={params.node.data.defaultValue ? Number(params.node.data.defaultValue) : null}
                                    onValueChange={value => onUpdate({ ...params.node.data, defaultValue: value?.toString() })} />;

                            case CustomFieldType.String:
                                return <Form.Control
                                    type="string"
                                    defaultValue={params.node.data.defaultValue}
                                    placeholder={"Default"}
                                    onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, defaultValue: ev.target.value })} />;

                            default:
                                return null;
                        }
                    },
                    minWidth: 200,
                    maxWidth: 200,
                }),
            columnBuilder.build("Required?", "isRequired",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, boolean>) {
                        return <>
                            <Form.Check type="switch"
                                id={`isRequired-${params.node.data.sortOrder}`}
                                className="d-inline-block"
                                checked={params.value}
                                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, isRequired: ev.target.checked })} />
                            <InfoIcon text={"Turn ON to require this custom field to new offers/contracts"} />
                        </>;
                    },
                    minWidth: 70,
                    maxWidth: 70,
                }),
            columnBuilder.build("ERP format", "erpFormat",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.erpFormat}
                            placeholder={"ERP format"}
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, erpFormat: ev.target.value })} />;
                    },
                    minWidth: 200,
                }),
            columnBuilder.build("Mapped field", "mappedField",
                {
                    cellRenderer: function (params: RenderParameters<CustomField, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.mappedField}
                            placeholder={"Mapped field"}
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ...params.node.data, mappedField: ev.target.value })} />;
                    },
                    minWidth: 200,
                }),
            columnBuilder.build("", null,
                {
                    cellRenderer: function (params: RenderParameters<CustomField>) {
                        return <button className="link-button" onClick={() => onRemove(params.node.data)}>
                            <i className="material-icons">delete</i>
                        </button>;
                    },
                    minWidth: 30,
                    maxWidth: 30,
                }),
        ];
    }

    function getTypeText(customFieldType: CustomFieldType): string {
        switch (customFieldType) {
            case CustomFieldType.String: return "Text";
            case CustomFieldType.Int: return "Number";
            case CustomFieldType.Decimal: return "Precision number";
            case CustomFieldType.Date: return "Date";
            case CustomFieldType.DateTime: return "Date & time";
            case CustomFieldType.DateRange: return "Date range";
            case CustomFieldType.SingleSelect: return "Select";
            case CustomFieldType.MultiSelect: return "Multi-select";
            default: return CustomFieldType[customFieldType];
        }
    }

    function isAllowedOptionsFieldSupported(customField: CustomField): boolean {
        const supportedTypes = [CustomFieldType.SingleSelect, CustomFieldType.MultiSelect];
        return supportedTypes.includes(customField.type);
    }

    function isDefaultFieldSupported(customField: CustomField): boolean {
        const supportedTypes = [CustomFieldType.String, CustomFieldType.Int, CustomFieldType.Decimal, CustomFieldType.Date, CustomFieldType.DateTime, CustomFieldType.SingleSelect];
        return supportedTypes.includes(customField.type);
    }

    function isMinMaxFieldsSupported(customField: CustomField): boolean {
        const supportedTypes = [CustomFieldType.Int, CustomFieldType.Decimal, CustomFieldType.Date, CustomFieldType.DateTime];
        return supportedTypes.includes(customField.type);
    }

    function onUpdate(customField: CustomField): void {
        if (!isAllowedOptionsFieldSupported(customField))
            customField.allowedOptions = [];

        if (!isMinMaxFieldsSupported(customField)) {
            customField.minValue = null;
            customField.maxValue = null;
        }

        if (!isDefaultFieldSupported(customField))
            customField.defaultValue = null;

        setSelectedCustomField(customField);
        props.onUpdate(customField);
    }

    function openOfferContractsModal(customField: CustomField): void {
        setSelectedCustomField(customField);
        setShowOffersContractsModal(true);
    }

    function handleOfferContractTypesSubmit(offerTypes: OfferTypeEnum[], contractTypes: ContractType[]): void {
        props.onUpdate({
            ...selectedCustomField,
            allowedOfferTypes: offerTypes,
            allowedContractTypes: contractTypes,
        });
        setShowOffersContractsModal(false);
    }

    function openCommoditiesModal(customField: CustomField): void {
        setSelectedCustomField(customField);
        setShowCommoditiesModal(true);
    }

    async function handleCommoditiesSubmit(commodities: number[]): Promise<void> {
        props.onUpdate({ ...selectedCustomField, allowedCommodities: commodities });
        setShowCommoditiesModal(false);
    }

    function openAllowedOptionsModal(customField: CustomField): void {
        setSelectedCustomField(customField);
        setShowCustomFieldOptionsModal(true);
    }

    function handleAllowedOptionsSubmit(customFieldOptions: CustomFieldOption[]): void {
        onUpdate({ ...selectedCustomField, allowedOptions: customFieldOptions });

        setSelectedCustomField(null);
        setShowCustomFieldOptionsModal(false);
    }

    function getBuySellValue(customField: CustomField): string {
        if (customField == null || (customField.allowPurchase && customField.allowSale)) return BuySellValue;
        return customField.allowPurchase ? BuyValue : SellValue;
    }

    function onBuySellValueSelected(customField: CustomField, value: string): void {
        const canBuy = value !== SellValue;
        const canSell = value !== BuyValue;
        props.onUpdate({ ...customField, allowPurchase: canBuy, allowSale: canSell });
    }

    function onRemove(customField: CustomField): void {
        confirmModal.show(
            `Remove Custom Field - ${customField.name}`,
            <>Do you really want to remove this custom field - {customField.name}</>,
            () => props.onRemove(customField),
            undefined,
            false,
        );
    }

    return <>
        <div className="ag-theme-alpine">
            <div className="row table-header-row ml-0 mr-0">
                <div className="col">
                    <div className="page-sub-header">Custom Fields</div>
                </div>
                <div className="col action-bar">
                    <button className="btn btn-link ml-auto" onClick={props.onAdd}>
                        <i className="material-icons">add_circle_outline</i>
                        Add Custom Field
                    </button>
                </div>
            </div>

            <AgGridReact
                {...defaultGridOptions}
                columnDefs={getColumns()}
                rowHeight={32}
                rowData={props.customFields ?? []}
                getRowId={params => `custom-field-${params.data.id}-${params.data.sortOrder}`}
                domLayout="autoHeight"
                onGridReady={params => params.api.sizeColumnsToFit()}
            />
        </div>

        <OfferContractTypesModal
            appendTitle={selectedCustomField?.name}
            showModal={showOffersContractsModal}
            offerTypes={OfferAndPricingTypes}
            contractTypes={AllContractTypes}
            existingOfferTypes={selectedCustomField?.allowedOfferTypes}
            existingContractTypes={selectedCustomField?.allowedContractTypes}
            handleSubmit={handleOfferContractTypesSubmit}
            handleCancel={() => {
                setSelectedCustomField(null);
                setShowOffersContractsModal(false);
            }} />

        <CommoditiesModal
            appendTitle={selectedCustomField?.name}
            showModal={showCommoditiesModal}
            commodities={props.commodities ?? []}
            existingCommodities={selectedCustomField?.allowedCommodities}
            isReadOnly={false}
            handleSubmit={handleCommoditiesSubmit}
            handleCancel={() => {
                setSelectedCustomField(null);
                setShowCommoditiesModal(false);
            }} />

        <CustomFieldOptionsModal
            showModal={showCustomFieldOptionsModal}
            customField={selectedCustomField}
            onSubmit={handleAllowedOptionsSubmit}
            onHide={() => {
                setSelectedCustomField(null);
                setShowCustomFieldOptionsModal(false);
            }} />

        <ConfirmationModal modalState={confirmModal.state} />
    </>;
};

export default ManageCustomFields;